(ns dre.components.banners
  (:require
   ["antd" :refer [Alert]]
   [dre.app.events :as app-events]
   [dre.config :as-alias conf]
   [dre.i18n.views :refer [tr]]
   [dre.storage.events :refer [load-ls set-ls]]
   [dre.utils.bem :as bem]
   [dre.utils :as utils]
   [reagent.core :as r]
   [re-frame.core :as rf]))

(def component-name :banners)

(defn banner
  "Show dismissable maintanence messages/training messages in the application
   
   Args:
   - message (string/react-element): Banner message/component to be shown.
   - banner-id (symbol): unique banner identifier .

  Examples:
    - [banner {:message \"Planned System Maintenance on 26 July 2025 where the system will be completely down.\"
             :banner-id :maintenance-banner}]
   
    - [banner {:message (r/as-element [:span [tr \"Attend a LitSearch training webinar.\"]
                                    \" \"
                                    [:a {:target \"_blank\" :href \"https://info.drevidence.com/search/training\"}
                                     [tr \"Click here to register.\"]]])
            :banner-id :training-banner}]"
  [{:keys [message banner-id]}]
  (let [show? (r/atom true)
        user @(rf/subscribe [::app-events/user])
        env @(rf/subscribe [::conf/get :env])
        last-login (-> user :last-login)
        last-login-js (if (nil? last-login)
                        (.now js/Date)
                        (js/Date. last-login))
        last-accept-datetime (:last-accept-datetime (load-ls :maintenance-banner))
        last-accept-datetime (if (nil? last-accept-datetime)
                               (js/Date. 1700 1 1)
                               last-accept-datetime)
        close!
        (fn [_e]
          (set-ls banner-id {:last-accept-datetime (.now js/Date)})
          (reset! show? false))]
    (fn []
      (when (and
             @show?
             (or (utils/dev-env? env) (utils/prod-env? env) (utils/beta-env? env))
             (not (= "covid19" (:email user)))
             (< last-accept-datetime last-login-js))
        [:> Alert
         {:class (bem/be component-name :base)
          :message [tr message]
          :type "warning"
          :closable true
          :on-close close!}]))))

;;;; Platform migration banner
;;;;
;;;; Intentionally standalone: it shares no state, storage or behaviour with
;;;; `banner` above (which gates on login/env and persists in localStorage),
;;;; and it is a LitSearch-only copy of the RegIntel banner - the two are kept
;;;; separate on purpose so either can change without affecting the other.

(def ^:private platform-migration-email "support@doctorevidence.com")

(def ^:private platform-migration-body
  (str "Hello Dr. Evidence team,\n\n"
       "I would like to schedule my training and migration to the new "
       "Dr. Evidence Intelligence Platform.\n\n"
       "Please let me know which times are available.\n\n"
       "Thank you."))

(def ^:private platform-migration-dismissed-key
  "docsearch/platform-migration-banner-dismissed")

(defn- platform-migration-dismissed?
  "True when the banner was dismissed earlier in this browser session.
   Session storage is cleared when the session ends, so the banner comes back
   the next time the user starts one. Returns false when storage is unavailable
   (private mode, blocked cookies), which just shows the banner."
  []
  (try
    (some? (.getItem js/sessionStorage platform-migration-dismissed-key))
    (catch :default _e false)))

(defn- dismiss-platform-migration!
  "Remembers the dismissal for the rest of this browser session."
  []
  (try
    (.setItem js/sessionStorage platform-migration-dismissed-key "true")
    (catch :default _e nil)))

(defn- platform-migration-mailto
  "Builds a mailto href for `email` with a URL-encoded prefilled `body`."
  [email body]
  (str "mailto:" email "?body=" (js/encodeURIComponent body)))

(defn- platform-migration-message
  []
  (r/as-element
    [:span
     [tr "The NEW Dr. Evidence Intelligence Platform is here and better than ever!"]
     " "
     [:a {:class (bem/be component-name :platform-migration-link)
          :href (platform-migration-mailto platform-migration-email
                                           platform-migration-body)}
      [tr "CLICK HERE"]]
     " "
     [tr "to schedule your training & migration."]]))

(defn platform-migration-banner
  "Announce the new Intelligence Platform and link to scheduling training & migration.
   Dismissable for the current browser session only - it reappears in the next one."
  []
  (let [show? (r/atom (not (platform-migration-dismissed?)))
        close!
        (fn [_e]
          (dismiss-platform-migration!)
          (reset! show? false))]
    (fn []
      (when @show?
        [:> Alert
         {:class (bem/be component-name :platform-migration)
          :message (platform-migration-message)
          :type "info"
          :closable true
          :on-close close!}]))))

(defn banners
  []
  [:div
   {:class component-name}
   ;;Banners here
   ])